import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, of, timer, zip } from 'rxjs';

import { ENVIRONMENT_SERVICE } from '@dibsondance/shared-environment-base';

/** Module-level flag to ensure wakeup only happens once across all instances */
let wakeupInitiated = false;

/** Minimum time to show splash screen (ms) */
const MIN_SPLASH_DURATION = 800;

/**
 * Service that wakes up the backend server on app startup.
 * QA environment goes to sleep when idle, so we call the version endpoint
 * to wake it up before showing the main app.
 */
@Injectable({ providedIn: 'root' })
export class BackendWakeupService {
  private readonly http = inject(HttpClient);
  private readonly environmentService = inject(ENVIRONMENT_SERVICE);

  /** Signal indicating whether the backend is ready */
  readonly isBackendReady = signal(false);

  /** Signal for any error that occurred */
  readonly error = signal<string | null>(null);

  constructor() {
    this.wakeUpBackend();
  }

  /**
   * Wakes up the backend by calling the version endpoint once.
   * Shows splash screen for at least MIN_SPLASH_DURATION ms.
   */
  private wakeUpBackend(): void {
    // Module-level flag ensures this only runs once ever
    if (wakeupInitiated) {
      return;
    }
    wakeupInitiated = true;

    const apiUrl = this.environmentService.environment.apiUrl;
    const versionUrl = `${apiUrl}api/version`;

    const versionCall$ = this.http.get<unknown>(versionUrl).pipe(
      catchError((err) => {
        // CORS errors show as errors even with 200 status - this is expected in dev
        // The request still wakes up the backend, so we continue normally
        console.log('Backend wakeup call completed (CORS may block response reading)');
        return of(null);
      })
    );

    // Wait for both: API response AND minimum splash duration
    zip(versionCall$, timer(MIN_SPLASH_DURATION)).subscribe(() => {
      this.isBackendReady.set(true);
    });
  }
}
